import { useState, useEffect, useCallback, useRef } from "react";
import { useCustomFields } from "../../hooks/useCustomFields";
import {
  convertDependentFieldForSubmission,
  convertDependentFieldFromAPI,
  customFieldValuesAreEqual,
} from "../../utils/customFieldUtils";
import {
  CustomField,
  CustomFieldOption,
  CustomFieldDependencyLevel,
} from "./types";

const DEPENDENT_FIELD = "DEPENDENT_FIELD";
const MULTIPLE_OPTION = "MULTIPLE_OPTION";
const BOOLEAN_TYPES = ["BOOLEAN", "SWITCH"];
const DROPDOWN_RADIO_TYPES = ["DROPDOWN", "DROP_DOWN", "RADIO", "RADIO_OPTION"];
const OPTION_FIELD_TYPES = [...DROPDOWN_RADIO_TYPES, MULTIPLE_OPTION];
const DECIMAL_TYPES = [
  "NUMBER",
  "DECIMAL",
  "DECIMAL_VALUE",
  "DECIMAL_VALUES",
  "FLOAT",
  "FLOAT_VALUES",
  "NUMERIC",
  "DOUBLE",
  "DOUBLE_VALUES",
];
const INTEGER_TYPES = [
  "INTEGER",
  "INTEGER_VALUES",
  "INTEGER_SMALL",
  "INTEGER_LARGE",
  "SMALL_INTEGER",
  "SMALL_INTEGER_VALUES",
  "SMALLINT",
  "LARGE_INTEGER",
  "LARGE_INTEGER_VALUES",
  "BIGINT",
];

export interface CustomFieldValidationResult {
  valid: boolean;
  errors: { [id: number]: string };
  missingLabels: string[];
}

export interface UseCustomFieldsControllerReturn {
  customFields: CustomField[];
  customFieldValues: { [id: number]: any };
  customFieldErrors: { [id: number]: string };
  loading: boolean;
  error: string | null;
  handleChange: (fieldId: number, value: any) => void;
  fetchFieldOptions: (fieldId: number) => Promise<CustomFieldOption[]>;
  fetchDependencyLevels: (
    fieldId: number
  ) => Promise<CustomFieldDependencyLevel[]>;
  validateRequired: () => CustomFieldValidationResult;
  getMissingLabels: () => string[];
  hasChanges: () => boolean;
  resetToDefaults: () => Promise<void>;
  buildSubmissionPayload: () => Promise<
    Array<{ custom_field_id: number; custom_field_value: any }>
  >;
}

const getFieldType = (field: CustomField): string =>
  field.custom_field_data_type?.custom_field_data_type_key;

const fieldHasDefaultValue = (field: CustomField): boolean =>
  field.default_value !== null &&
  field.default_value !== undefined &&
  field.default_value !== "";

const parseDefaultValue = (raw: any): any => {
  let value = raw;
  if (
    typeof value === "string" &&
    (value.startsWith("[") || value.startsWith("{"))
  ) {
    try {
      value = JSON.parse(value);
    } catch (e) {}
  }
  return value;
};

const hasUserValueFor = (fieldType: string, value: any): boolean => {
  if (BOOLEAN_TYPES.includes(fieldType)) {
    return value !== null && value !== undefined;
  }
  if (fieldType === MULTIPLE_OPTION) {
    return Array.isArray(value) && value.length > 0;
  }
  if (fieldType === DEPENDENT_FIELD) {
    return !!(
      value &&
      typeof value === "object" &&
      Object.keys(value).length > 0
    );
  }
  return value !== null && value !== undefined && value !== "";
};

export function useCustomFieldsController(
  tenantId: string | null,
  entityKey: string,
  record: any | null,
  mode: "create" | "edit"
): UseCustomFieldsControllerReturn {
  const {
    customFields,
    loading,
    error,
    fetchFieldOptions,
    fetchDependencyLevels,
  } = useCustomFields(tenantId, entityKey);

  const [customFieldValues, setCustomFieldValues] = useState<{
    [id: number]: any;
  }>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<{
    [id: number]: string;
  }>({});
  const [dependencyLevelsMap, setDependencyLevelsMap] = useState<{
    [fieldId: number]: CustomFieldDependencyLevel[];
  }>({});
  const [optionsMap, setOptionsMap] = useState<{
    [fieldId: number]: CustomFieldOption[];
  }>({});

  const originalCustomFieldValuesRef = useRef<{ [id: number]: any }>({});
  const hasCapturedOriginalRef = useRef(false);

  const computeDefaultValues = useCallback(async (): Promise<{
    [id: number]: any;
  }> => {
    const defaultValues: { [id: number]: any } = {};

    for (const field of customFields) {
      if (!fieldHasDefaultValue(field)) continue;

      const value = parseDefaultValue(field.default_value);
      const fieldType = getFieldType(field);

      if (
        fieldType === DEPENDENT_FIELD &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        try {
          const [levels, options] = await Promise.all([
            fetchDependencyLevels(field.custom_field_id),
            fetchFieldOptions(field.custom_field_id),
          ]);

          const convertedValue: { [levelId: number]: number } = {};

          for (const [levelName, optionLabel] of Object.entries(value)) {
            const level = levels.find(
              (l) => l.dependency_level === levelName
            );
            if (level) {
              const option = options.find(
                (o) =>
                  o.choice_label === optionLabel &&
                  o.custom_field_dependency_level_id ===
                    level.custom_field_dependency_level_id
              );
              if (option) {
                convertedValue[level.custom_field_dependency_level_id] =
                  option.custom_field_choice_option_id;
              }
            }
          }

          if (Object.keys(convertedValue).length > 0) {
            defaultValues[field.custom_field_id] = convertedValue;
          }
        } catch (err) {
          console.error(
            "Error converting dependent field default value:",
            err
          );
        }
      } else {
        defaultValues[field.custom_field_id] = value;
      }
    }

    return defaultValues;
  }, [customFields, fetchDependencyLevels, fetchFieldOptions]);

  useEffect(() => {
    let cancelled = false;

    if (
      mode === "edit" &&
      record &&
      Array.isArray(record.custom_fields) &&
      customFields.length > 0
    ) {
      const customFieldValuesMap: { [id: number]: any } = {};
      const asyncUpdates: Promise<void>[] = [];

      record.custom_fields.forEach((cf: any) => {
        if (!cf.custom_field_id) return;

        let value: any = null;

        if (
          cf.custom_field_value &&
          typeof cf.custom_field_value === "object" &&
          Object.keys(cf.custom_field_value).length > 0
        ) {
          value =
            cf.custom_field_value.custom_field_value ||
            cf.custom_field_value.value ||
            cf.custom_field_value;
        } else if (
          cf.custom_field_value !== null &&
          cf.custom_field_value !== undefined &&
          cf.custom_field_value !== ""
        ) {
          value = cf.custom_field_value;
        }

        if (value === null || value === undefined) return;

        const fieldDef = customFields.find(
          (f) => f.custom_field_id === cf.custom_field_id
        );

        if (!fieldDef) {
          customFieldValuesMap[cf.custom_field_id] = value;
          return;
        }

        const fieldType = getFieldType(fieldDef);

        if (fieldType === DEPENDENT_FIELD) {
          const convertedValue = convertDependentFieldFromAPI(value);
          if (convertedValue) {
            customFieldValuesMap[cf.custom_field_id] = convertedValue;
          }
        } else if (OPTION_FIELD_TYPES.includes(fieldType)) {
          const asyncUpdate = fetchFieldOptions(cf.custom_field_id)
            .then((options) => {
              let mappedValue = value;

              if (fieldType === MULTIPLE_OPTION && Array.isArray(value)) {
                mappedValue = value
                  .map((val: any) => {
                    if (typeof val === "string") {
                      const option = options.find(
                        (opt) => opt.choice_label === val
                      );
                      return option
                        ? option.custom_field_choice_option_id
                        : val;
                    }
                    return val;
                  })
                  .filter((val: any) => val !== null && val !== undefined);
              } else if (
                DROPDOWN_RADIO_TYPES.includes(fieldType) &&
                typeof value === "string"
              ) {
                const option = options.find(
                  (opt) => opt.choice_label === value
                );
                mappedValue = option
                  ? option.custom_field_choice_option_id
                  : value;
              }

              customFieldValuesMap[cf.custom_field_id] = mappedValue;
            })
            .catch((err) => {
              console.error("Error loading options for field mapping:", err);
              customFieldValuesMap[cf.custom_field_id] = value;
            });
          asyncUpdates.push(asyncUpdate);
        } else {
          customFieldValuesMap[cf.custom_field_id] = value;
        }
      });

      const commit = () => {
        if (cancelled) return;
        setCustomFieldValues(customFieldValuesMap);
        if (!hasCapturedOriginalRef.current) {
          originalCustomFieldValuesRef.current = customFieldValuesMap;
          hasCapturedOriginalRef.current = true;
        }
      };

      if (asyncUpdates.length > 0) {
        Promise.all(asyncUpdates).then(commit);
      } else {
        commit();
      }
    } else if (mode === "create" && customFields.length > 0) {
      computeDefaultValues().then((defaultValues) => {
        if (cancelled) return;
        setCustomFieldValues(defaultValues);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [mode, record, customFields, fetchFieldOptions, computeDefaultValues]);

  const handleChange = useCallback(
    (fieldId: number, value: any) => {
      setCustomFieldValues((prev) => ({
        ...prev,
        [fieldId]: value,
      }));

      setCustomFieldErrors((prev) => {
        if (!prev[fieldId]) return prev;
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });

      const field = customFields.find((f) => f.custom_field_id === fieldId);
      if (
        field &&
        getFieldType(field) === DEPENDENT_FIELD &&
        !dependencyLevelsMap[fieldId]
      ) {
        Promise.all([
          fetchDependencyLevels(fieldId),
          fetchFieldOptions(fieldId),
        ])
          .then(([levels, options]) => {
            setDependencyLevelsMap((prev) => ({ ...prev, [fieldId]: levels }));
            setOptionsMap((prev) => ({ ...prev, [fieldId]: options }));
          })
          .catch((err) => {
            console.error("Error loading dependency data:", err);
          });
      }
    },
    [customFields, dependencyLevelsMap, fetchDependencyLevels, fetchFieldOptions]
  );

  const computeMissing = useCallback((): {
    errors: { [id: number]: string };
    missingLabels: string[];
  } => {
    const newErrors: { [id: number]: string } = {};
    const missingLabels: string[] = [];

    customFields.forEach((field) => {
      if (!field.is_required) return;

      const value = customFieldValues[field.custom_field_id];
      const fieldType = getFieldType(field);
      const hasDefault = fieldHasDefaultValue(field);
      const hasValue = hasUserValueFor(fieldType, value);

      if (!hasValue && !hasDefault) {
        newErrors[
          field.custom_field_id
        ] = `${field.custom_field_display_name} is required`;
        missingLabels.push(field.custom_field_display_name);
      }
    });

    return { errors: newErrors, missingLabels };
  }, [customFields, customFieldValues]);

  const getMissingLabels = useCallback((): string[] => {
    return computeMissing().missingLabels;
  }, [computeMissing]);

  const hasChanges = useCallback((): boolean => {
    if (mode !== "edit") return false;
    const original = originalCustomFieldValuesRef.current;
    const ids = Array.from(
      new Set([
        ...Object.keys(original).map(Number),
        ...Object.keys(customFieldValues).map(Number),
      ])
    );
    return ids.some(
      (id) => !customFieldValuesAreEqual(customFieldValues[id], original[id])
    );
  }, [mode, customFieldValues]);

  const validateRequired = useCallback((): CustomFieldValidationResult => {
    const { errors: newErrors, missingLabels } = computeMissing();

    setCustomFieldErrors(newErrors);

    return {
      valid: Object.keys(newErrors).length === 0,
      errors: newErrors,
      missingLabels,
    };
  }, [computeMissing]);

  const resetToDefaults = useCallback(async () => {
    const defaultValues = await computeDefaultValues();
    setCustomFieldValues(defaultValues);
    setCustomFieldErrors({});
    originalCustomFieldValuesRef.current = {};
    hasCapturedOriginalRef.current = true;
  }, [computeDefaultValues]);

  const buildSubmissionPayload = useCallback(async (): Promise<
    Array<{ custom_field_id: number; custom_field_value: any }>
  > => {
    const isEdit = mode === "edit";
    const result: Array<{
      custom_field_id: number;
      custom_field_value: any;
    }> = [];

    const localOptionsMap: { [fieldId: number]: CustomFieldOption[] } = {
      ...optionsMap,
    };
    const localDependencyLevelsMap: {
      [fieldId: number]: CustomFieldDependencyLevel[];
    } = { ...dependencyLevelsMap };

    const loadPromises: Promise<void>[] = [];

    customFields.forEach((field) => {
      const value = customFieldValues[field.custom_field_id];
      const hasDefault = fieldHasDefaultValue(field);
      const hasAnyValue = value !== null && value !== undefined && value !== "";

      if (!hasAnyValue && !hasDefault) return;

      const fieldType = getFieldType(field);

      if (
        fieldType === DEPENDENT_FIELD &&
        !localDependencyLevelsMap[field.custom_field_id]
      ) {
        const promise = Promise.all([
          fetchDependencyLevels(field.custom_field_id),
          fetchFieldOptions(field.custom_field_id),
        ]).then(([levels, options]) => {
          localDependencyLevelsMap[field.custom_field_id] = levels;
          localOptionsMap[field.custom_field_id] = options;
          setDependencyLevelsMap((prev) => ({
            ...prev,
            [field.custom_field_id]: levels,
          }));
          setOptionsMap((prev) => ({
            ...prev,
            [field.custom_field_id]: options,
          }));
        });
        loadPromises.push(promise);
      }

      if (
        OPTION_FIELD_TYPES.includes(fieldType) &&
        !localOptionsMap[field.custom_field_id]
      ) {
        const promise = fetchFieldOptions(field.custom_field_id).then(
          (options) => {
            localOptionsMap[field.custom_field_id] = options;
            setOptionsMap((prev) => ({
              ...prev,
              [field.custom_field_id]: options,
            }));
          }
        );
        loadPromises.push(promise);
      }
    });

    if (loadPromises.length > 0) {
      await Promise.all(loadPromises);
    }

    customFields.forEach((field) => {
      let value = customFieldValues[field.custom_field_id];
      const originalValue =
        originalCustomFieldValuesRef.current[field.custom_field_id];
      const fieldType = getFieldType(field);
      const hasDefault = fieldHasDefaultValue(field);

      const hasUserValue = hasUserValueFor(fieldType, value);
      const originalHadValue = hasUserValueFor(fieldType, originalValue);

      const wasCleared = isEdit && originalHadValue && !hasUserValue;
      const hasChanged =
        isEdit && !customFieldValuesAreEqual(value, originalValue);

      if (isEdit && !hasChanged && !wasCleared) {
        return;
      }

      if (!isEdit && !hasUserValue && !hasDefault) {
        return;
      }

      if (wasCleared) {
        value = null;
      } else if (!hasUserValue && hasDefault) {
        value = parseDefaultValue(field.default_value);
      }

      let processedValue = value;

      if (wasCleared) {
        processedValue = null;
      } else {
        const options =
          localOptionsMap[field.custom_field_id] || field.options || [];

        if (fieldType === DEPENDENT_FIELD) {
          const dependencyLevels =
            localDependencyLevelsMap[field.custom_field_id] || [];
          processedValue = convertDependentFieldForSubmission(
            value,
            dependencyLevels,
            options
          );
        } else if (fieldType === MULTIPLE_OPTION && Array.isArray(value)) {
          processedValue = value
            .map((val: any) => {
              if (typeof val === "string") {
                const option = options.find(
                  (opt) => opt.choice_label === val
                );
                return option ? option.custom_field_choice_option_id : val;
              }
              return val;
            })
            .filter((val: any) => val !== null && val !== undefined);
        } else if (DROPDOWN_RADIO_TYPES.includes(fieldType)) {
          if (typeof value === "string" && options.length > 0) {
            const option = options.find((opt) => opt.choice_label === value);
            processedValue = option
              ? option.custom_field_choice_option_id
              : Number(value);
          } else {
            processedValue = Number(value);
          }
        } else if (BOOLEAN_TYPES.includes(fieldType)) {
          if (typeof value === "string") {
            processedValue =
              value === "true" || value === "TRUE" || value === "1";
          } else {
            processedValue = Boolean(value);
          }
        } else if (DECIMAL_TYPES.includes(fieldType)) {
          processedValue = Number(value);
        } else if (INTEGER_TYPES.includes(fieldType)) {
          processedValue = parseInt(value, 10);
        }
      }

      result.push({
        custom_field_id: field.custom_field_id,
        custom_field_value: processedValue,
      });
    });

    return result;
  }, [
    customFields,
    customFieldValues,
    mode,
    optionsMap,
    dependencyLevelsMap,
    fetchDependencyLevels,
    fetchFieldOptions,
  ]);

  return {
    customFields,
    customFieldValues,
    customFieldErrors,
    loading,
    error,
    handleChange,
    fetchFieldOptions,
    fetchDependencyLevels,
    getMissingLabels,
    hasChanges,
    validateRequired,
    resetToDefaults,
    buildSubmissionPayload,
  };
}
